import React, { useState } from "react";
import { AlertCircle, Copy, Search } from "lucide-react";
import { lookupAudioChunks } from "./api_utils";
import { environment } from "../../config/environment";

const INITIAL_FORM = {
  doctorName: "",
  month: "",
  day: "",
  patientId: "",
};

const AudioLookup = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [results, setResults] = useState([]);
  const [lookupMeta, setLookupMeta] = useState({
    bucket: "",
    searchPrefix: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [copiedConversationId, setCopiedConversationId] = useState("");

  const prefixPreview = `${form.doctorName.trim() || "{doctor_name}"}/${form.month.toString().trim() || "{month}"}/${form.day.toString().trim() || "{day}"}/`;

  const updateField = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const doctorName = form.doctorName.trim();
    const month = form.month.toString().trim();
    const day = form.day.toString().trim();
    const patientId = form.patientId.trim();

    if (!doctorName || !month || !day || !patientId) {
      setError("All fields are required.");
      setResults([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const result = await lookupAudioChunks({
        doctorName,
        month,
        day,
        patientId,
      });

      setResults(result.data);
      setLookupMeta({
        bucket: result.bucket,
        searchPrefix: result.searchPrefix,
      });
    } catch (requestError) {
      setResults([]);
      setLookupMeta({
        bucket: "",
        searchPrefix: `${doctorName}/${month}/${day}/`,
      });
      setError(requestError.message || "Failed to look up audio chunks.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndCopyJson = async (conversationId, totalChunks) => {
    const payload = {
      httpMethod: "POST",
      body: JSON.stringify({
        conversation_id: conversationId,
        total_chunks: totalChunks,
      }),
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopiedConversationId(conversationId);
      setTimeout(() => {
        setCopiedConversationId((current) =>
          current === conversationId ? "" : current
        );
      }, 2000);
    } catch (copyError) {
      setError(copyError.message || "Failed to copy JSON to clipboard.");
    }
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Audio Lookup</h2>
        <p className="text-gray-600 mt-1">
          Search audio chunk files under a single doctor/month/day prefix and
          group matches by conversation.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Doctor Name
            </label>
            <input
              type="text"
              value={form.doctorName}
              onChange={updateField("doctorName")}
              placeholder="Dr.LuisMacias"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Month
            </label>
            <input
              type="text"
              value={form.month}
              onChange={updateField("month")}
              placeholder="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Day
            </label>
            <input
              type="text"
              value={form.day}
              onChange={updateField("day")}
              placeholder="12"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient ID
            </label>
            <input
              type="text"
              value={form.patientId}
              onChange={updateField("patientId")}
              placeholder="1202680"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-gray-600">
            <div>
              Search prefix:{" "}
              <span className="font-mono text-gray-900">{prefixPreview}</span>
            </div>
            <div>
              Bucket:{" "}
              <span className="font-mono text-gray-900">
                {lookupMeta.bucket || environment.realtimeAudioBucket}
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="h-4 w-4" />
            {loading ? "Searching..." : "Search Audio Chunks"}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {hasSearched && !loading && !error && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                Results ({results.length})
              </h3>
              <span className="text-xs text-gray-500 font-mono">
                {lookupMeta.searchPrefix || prefixPreview}
              </span>
            </div>

            {results.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-6 text-sm text-gray-600">
                No matching conversations or audio chunk files were found for
                this patient under the requested prefix.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Conversation ID
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Total Chunks
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result) => (
                      <tr
                        key={result.conversation_id}
                        className="border-b border-gray-100 last:border-b-0"
                      >
                        <td className="py-3 px-4 font-mono text-xs text-gray-900 break-all">
                          {result.conversation_id}
                        </td>
                        <td className="py-3 px-4 text-gray-900">
                          {result.total_chunks}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            type="button"
                            onClick={() =>
                              handleCreateAndCopyJson(
                                result.conversation_id,
                                result.total_chunks
                              )
                            }
                            className="inline-flex items-center gap-2 px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-xs"
                          >
                            <Copy className="h-3.5 w-3.5" />
                            {copiedConversationId === result.conversation_id
                              ? "Copied"
                              : "Create and Copy JSON"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default AudioLookup;
